import { useState, useEffect } from 'react'
import { StickyNote, X, Plus, Trash2 } from 'lucide-react'
import { useT } from '../i18n/index.js'

const STORAGE_KEY = 'patrimia-quick-notes'

interface QuickNote {
  id: string
  text: string
  createdAt: string
}

function loadNotes(): QuickNote[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveNotes(notes: QuickNote[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notes))
}

/**
 * F27 — Notes rapides (FAB flottant).
 * Bouton en bas a droite, ouvre un panneau de notes.
 */
export function QuickNotes() {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState<QuickNote[]>(loadNotes)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    saveNotes(notes)
  }, [notes])

  const addNote = () => {
    const text = draft.trim()
    if (!text) return
    setNotes((prev) => [
      { id: crypto.randomUUID(), text, createdAt: new Date().toISOString() },
      ...prev,
    ])
    setDraft('')
  }

  const removeNote = (id: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== id))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      addNote()
    }
  }

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-[var(--color-primary)] text-white shadow-lg hover:opacity-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2"
        aria-label={open ? t.common.close : t.quickNotes?.title || 'Notes rapides'}
      >
        {open ? <X size={22} /> : <StickyNote size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-80 max-h-[60vh] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              {t.quickNotes?.title || 'Notes rapides'}
            </h3>
            <span className="text-xs text-[var(--color-text-disabled)]">{notes.length}</span>
          </div>

          {/* Input */}
          <div className="p-3 border-b border-[var(--color-border-subtle)]">
            <div className="flex gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t.quickNotes?.placeholder || 'Ecrire une note...'}
                rows={2}
                className="flex-1 px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <button
                type="button"
                onClick={addNote}
                disabled={!draft.trim()}
                className="self-end min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
                aria-label={t.quickNotes?.add || 'Ajouter'}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {notes.length === 0 ? (
              <p className="text-center text-sm text-[var(--color-text-disabled)] py-4">
                {t.quickNotes?.empty || 'Aucune note'}
              </p>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className="group flex gap-2 p-2 rounded-md bg-[var(--color-background)] border border-[var(--color-border-subtle)]"
                >
                  <p className="flex-1 text-sm text-[var(--color-text)] whitespace-pre-wrap break-words">
                    {note.text}
                  </p>
                  <button
                    type="button"
                    onClick={() => removeNote(note.id)}
                    className="self-start min-h-[44px] min-w-[44px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[var(--color-text-disabled)] hover:text-[var(--color-error)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
                    aria-label={t.quickNotes?.delete || 'Supprimer la note'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  )
}
